import { GoogleLogin } from "@react-oauth/google";
import { useFormik } from "formik";
import { ArrowLeft, ArrowRight, Eye, EyeOff, Lock, Mail, Shield, User, Users } from "lucide-react";
import React, { useCallback, useContext, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import logo from "@/assets/altLogo.svg";
import image2 from "@/assets/fuundo_ondas.png";
import image1 from "@/assets/fuundo_ondas (1).png";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthContext } from "@/providers/AuthProvider";
import AuthLayout from "@/components/Layout/AuthLayout";

import "./Login.css";
import "@/styles/auth-theme.css";

const Login = () => {
  const navigate = useNavigate();
  const { userType } = useParams();
  const { signIn, signInAdmin, signInSales, signInWithGoogle } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // New state for view management: "choice" or "email-form"
  const [viewState, setViewState] = useState("choice");

  const form = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .email("Formato de email inválido.")
        .required("O campo e-mail é obrigatório!"),
      password: yup
        .string()
        .min(8, "Senha deve conter no mínimo 8 caracteres.")
        .required("O campo senha é obrigatório!"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage("");
        if (type === "Administrador") {
          await signInAdmin(values);
        } else if (type === "Afiliado") {
          await signInSales(values);
        } else {
          await signIn({ ...values, rememberMe });
          // PublicRoute handles redirect (including demo flow via /provisionar-demo)
        }
      } catch (error) {
        setErrorMessage("E-mail ou senha inválidos. Por favor, tente novamente.");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleType = () => {
    switch (userType) {
      case "admin":
        return "Administrador";
      case "vendas":
        return "Afiliado";
      default:
        return "Parceiro";
    }
  };

  const type = handleType();
  const [selectedUserType, setSelectedUserType] = useState(type);

  // Memoize Google login handlers to prevent unnecessary re-renders
  const handleGoogleSuccess = useCallback(
    async (credentialResponse) => {
      try {
        setLoading(true);
        setErrorMessage("");

        if (selectedUserType !== "Parceiro") {
          setErrorMessage("Login com Google disponível apenas para Parceiros.");
          setLoading(false);
          return;
        }

        if (credentialResponse.credential) {
          const result = await signInWithGoogle(credentialResponse.credential, { rememberMe });

          if (result?.isNewRegistration) {
            navigate("/obrigado", {
              state: {
                partnerName: result.partnerName,
                isNewRegistration: true,
              },
            });
          }
          // For existing users, PublicRoute handles redirect (including demo flow)
        } else {
          setErrorMessage("Resposta do Google inválida. Tente novamente.");
        }
      } catch (error) {
        console.error("Erro no login Google:", error);
        setErrorMessage("Erro ao fazer login com Google. Por favor, tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [selectedUserType, signInWithGoogle, navigate, rememberMe],
  );

  const handleGoogleError = useCallback(() => {
    setLoading(false);
    setErrorMessage("Erro ao fazer login com Google. Por favor, tente novamente.");
  }, []);

  const handleBackToChoice = () => {
    setViewState("choice");
    setErrorMessage("");
    form.resetForm();
  };

  const getUserTypeInfo = (userType) => {
    switch (userType) {
      case "Parceiro":
        return {
          title: "Parceiro SacGPT",
          description: "Empresas que melhoram o atendimento com IA",
          // Opção A: cliente padrão passa pela demonstração (escolher assistente) antes do cadastro
          signupRoute: "/demonstracao",
        };
      case "Afiliado":
        return {
          title: "Afiliado SacGPT",
          description: "Profissionais que divulgam nossos produtos",
          // Cadastro de vendas/afiliado desabilitado por enquanto (null esconde o botão "criar conta")
          signupRoute: null,
        };
      case "Administrador":
        return {
          title: "Administrador",
          description: "Acesso administrativo ao sistema",
          signupRoute: null,
        };
      default:
        return {
          title: "Usuário",
          description: "Acesse sua conta",
          signupRoute: null,
        };
    }
  };

  const getIcon = (userType) => {
    switch (userType) {
      case "Administrador":
        return <Shield className="w-5 h-5" />;
      case "Afiliado":
        return <Users className="w-5 h-5" />;
      default:
        return <User className="w-5 h-5" />;
    }
  };

  const currentUserInfo = getUserTypeInfo(selectedUserType);

  // Render the choice buttons view
  const renderChoiceView = () => (
    <div className="login-choice-view animate-fade-in">
      {/* Welcome Header */}
      <div className="login-welcome-header">
        <h1 className="login-welcome-title">Bem-vindo</h1>
        <p className="login-welcome-subtitle">Acesse sua conta para continuar</p>
      </div>

      {/* Google Login Button - Direct, no overlay (Chrome compatible) */}
      <div className={`google-login-container ${loading ? "loading" : ""}`}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          useOneTap={false}
          theme="filled_blue"
          size="large"
          text="continue_with"
          shape="pill"
          width="320"
          locale="pt-BR"
        />
      </div>

      {/* Divider */}
      <div className="login-choice-divider">
        <span>ou</span>
      </div>

      {/* Email Login Button - Secondary */}
      <button type="button" className="btn-email-choice" onClick={() => setViewState("email-form")}>
        <Mail className="w-5 h-5" />
        <span>Entrar com Email</span>
      </button>

      {/* Create Account Link - Tertiary */}
      {currentUserInfo.signupRoute && (
        <button
          type="button"
          className="btn-create-account-link"
          onClick={() => navigate(currentUserInfo.signupRoute)}
        >
          Criar nova conta <ArrowRight className="w-4 h-4 inline-block ml-1" />
        </button>
      )}
    </div>
  );

  // Render the email form view
  const renderEmailFormView = () => (
    <div className="login-email-form-view animate-slide-up">
      {/* Back Button */}
      <button type="button" className="btn-back-to-choice" onClick={handleBackToChoice}>
        <ArrowLeft className="w-4 h-4" />
        <span>Voltar</span>
      </button>

      {/* Error Message */}
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {/* Login Form */}
      <form onSubmit={form.handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email" className="auth-label">
            E-mail
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Digite o e-mail cadastrado na plataforma"
            value={form.values.email}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            className="auth-input"
            autoComplete="email"
          />
          {form.touched.email && form.errors.email && (
            <div className="auth-error">{form.errors.email}</div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="auth-label">
            Senha
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            placeholder="Digite sua senha"
            value={form.values.password}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            className="auth-input"
            autoComplete="current-password"
          />
          {form.touched.password && form.errors.password && (
            <div className="auth-error">{form.errors.password}</div>
          )}
        </div>

        {/* Submit Button */}
        <Button type="submit" disabled={loading} className="auth-button-primary w-full">
          {loading ? (
            <>
              <div className="auth-loading-spinner" />
              Carregando...
            </>
          ) : (
            <>
              <span>Entrar</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </Button>
      </form>

      {/* Secondary Actions */}
      <div className="login-form-secondary-actions">
        <div className="login-divider">
          <span>Não tem conta?</span>
        </div>

        {currentUserInfo.signupRoute && (
          <button
            type="button"
            className="btn-create-account-secondary"
            onClick={() => navigate(currentUserInfo.signupRoute)}
          >
            Criar nova conta
          </button>
        )}

        <button
          type="button"
          className="btn-forgot-password"
          onClick={() => navigate("/recuperacao", { state: { type: selectedUserType } })}
        >
          Esqueci minha senha
        </button>
      </div>
    </div>
  );

  if (type === "Parceiro") {
    return (
      <AuthLayout action={<><span>Ainda não tem conta?</span><Link to="/demonstracao">Criar conta grátis</Link></>}>
        <h1>Entrar</h1>
        <p className="partner-auth-description">Bem-vindo de volta. Acesse sua conta para continuar.</p>
        <div className="partner-auth-google">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="outline"
            size="large"
            text="continue_with"
            shape="pill"
            logo_alignment="left"
            width="400"
            locale="pt-BR"
          />
        </div>
        <div className="partner-auth-divider">ou entre com e-mail</div>
        {errorMessage && <p role="alert" className="partner-auth-error">{errorMessage}</p>}
        <form onSubmit={form.handleSubmit} noValidate>
          <div className="partner-auth-fields">
            <div className="partner-auth-field">
              <label htmlFor="email">E-mail</label>
              <div className="partner-auth-input">
                <Mail aria-hidden="true" />
                <input id="email" name="email" type="email" autoComplete="email" placeholder="voce@empresa.com.br" value={form.values.email} onChange={form.handleChange} onBlur={form.handleBlur} aria-invalid={!!(form.touched.email && form.errors.email)} aria-describedby={form.touched.email && form.errors.email ? 'login-email-error' : undefined} />
              </div>
              {form.touched.email && form.errors.email && <p id="login-email-error" className="partner-auth-error">{form.errors.email}</p>}
            </div>
            <div className="partner-auth-field">
              <label htmlFor="password">Senha</label>
              <div className="partner-auth-input">
                <Lock aria-hidden="true" />
                <input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="••••••••" value={form.values.password} onChange={form.handleChange} onBlur={form.handleBlur} aria-invalid={!!(form.touched.password && form.errors.password)} aria-describedby={form.touched.password && form.errors.password ? 'login-password-error' : undefined} />
                <button type="button" aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'} onClick={() => setShowPassword(value => !value)}>{showPassword ? <Eye /> : <EyeOff />}</button>
              </div>
              {form.touched.password && form.errors.password && <p id="login-password-error" className="partner-auth-error">{form.errors.password}</p>}
            </div>
          </div>
          <div className="partner-auth-options">
            <label><input type="checkbox" checked={rememberMe} onChange={event => setRememberMe(event.target.checked)} />Manter conectado</label>
            <Link className="partner-auth-link" to="/recuperacao" state={{ type: 'Parceiro' }}>Esqueci a senha</Link>
          </div>
          <button type="submit" className="partner-auth-submit" disabled={loading}>
            <ArrowRight aria-hidden="true" />{loading ? 'Entrando...' : 'Entrar na minha conta'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <div className="auth-container">
      <img
        src={image1}
        className="auth-bg-image auth-bg-image-1"
        alt="Background decoration"
        draggable={false}
      />
      <img
        src={image2}
        className="auth-bg-image auth-bg-image-2"
        alt="Background decoration"
        draggable={false}
      />

      <div className="auth-content">
        <div className="auth-logo-container">
          <a
            href="/"
            onClick={(e) => {
              if (viewState === "email-form") {
                e.preventDefault();
                handleBackToChoice();
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <img src={logo} alt="SacGPT Logo" className="auth-logo" />
          </a>
        </div>

        <Card className="auth-card">
          <CardContent className="space-y-6">
            {/* User Type Selector - Only for admin/vendas routes */}
            {(userType === "admin" || userType === "vendas") && (
              <>
                <div className="auth-user-type-selector">
                  <button
                    type="button"
                    className={`auth-user-type-tab ${selectedUserType === "Parceiro" ? "active" : ""}`}
                    onClick={() => {
                      setSelectedUserType("Parceiro");
                      setViewState("choice");
                      navigate("/");
                    }}
                  >
                    <User className="w-4 h-4" />
                    Parceiro
                  </button>
                  <button
                    type="button"
                    className={`auth-user-type-tab ${selectedUserType === "Afiliado" ? "active" : ""}`}
                    onClick={() => {
                      setSelectedUserType("Afiliado");
                      setViewState("choice");
                      navigate("/vendas");
                    }}
                  >
                    <Users className="w-4 h-4" />
                    Afiliado
                  </button>
                  {userType === "admin" && (
                    <button
                      type="button"
                      className={`auth-user-type-tab ${selectedUserType === "Administrador" ? "active" : ""}`}
                      onClick={() => {
                        setSelectedUserType("Administrador");
                        setViewState("choice");
                        navigate("/admin");
                      }}
                    >
                      <Shield className="w-4 h-4" />
                      Admin
                    </button>
                  )}
                </div>

                <div className="auth-user-type-info">
                  <h3>
                    {getIcon(selectedUserType)}
                    {currentUserInfo.title}
                  </h3>
                  <p>{currentUserInfo.description}</p>
                </div>
              </>
            )}

            {/* Display error message for Google login errors in choice view */}
            {viewState === "choice" && errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {/* Conditional View Rendering */}
            {selectedUserType === "Parceiro" ? (
              viewState === "choice" ? (
                renderChoiceView()
              ) : (
                renderEmailFormView()
              )
            ) : (
              // For Afiliado and Admin, show email form directly (no Google option)
              <div className="animate-fade-in">
                {errorMessage && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={form.handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="auth-label">
                      E-mail
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Digite seu e-mail"
                      value={form.values.email}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                      className="auth-input"
                      autoComplete="email"
                    />
                    {form.touched.email && form.errors.email && (
                      <div className="auth-error">{form.errors.email}</div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="auth-label">
                      Senha
                    </Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="Digite sua senha"
                      value={form.values.password}
                      onChange={form.handleChange}
                      onBlur={form.handleBlur}
                      className="auth-input"
                      autoComplete="current-password"
                    />
                    {form.touched.password && form.errors.password && (
                      <div className="auth-error">{form.errors.password}</div>
                    )}
                  </div>

                  <Button type="submit" disabled={loading} className="auth-button-primary w-full">
                    {loading ? (
                      <>
                        <div className="auth-loading-spinner" />
                        Carregando...
                      </>
                    ) : (
                      <>
                        <span>Entrar</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="space-y-4 mt-6">
                  {currentUserInfo.signupRoute && (
                    <Button
                      onClick={() => navigate(currentUserInfo.signupRoute)}
                      className="auth-button-signup w-full"
                    >
                      Criar Conta de Afiliado
                    </Button>
                  )}

                  <div className="text-center">
                    <Button
                      variant="link"
                      onClick={() =>
                        navigate("/recuperacao", { state: { type: selectedUserType } })
                      }
                      className="auth-link text-sm"
                    >
                      Esqueci minha senha
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <footer className="auth-footer">sacgpt.com.br © 2026 - Todos os Direitos Reservados</footer>
    </div>
  );
};

export default Login;
